import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Calendar,
  Clock,
  MessageCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserDeleteModal } from '@/components/modals/UserDeleteModal';
import {
  userManagementAPI,
  type User as ServiceUser,
} from '@/services/user-management';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'superadmin' | 'branch_admin' | 'owner';
  branch?: string;
  avatar?: string;
  lastLogin: string;
  createdAt: string;
  phone?: string;
  department?: string;
  permissions?: string[];
  isActive?: boolean;
}

const getRoleColor = (role: User['role']) => {
  switch (role) {
    case 'superadmin':
      return 'bg-destructive/10 text-destructive';
    case 'branch_admin':
      return 'bg-chart-2/10 text-chart-2';
    case 'owner':
      return 'bg-chart-4/10 text-chart-4';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getStatusColor = (status: User['isActive']) => {
  return status === true
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
};

const formatRole = (role: User['role']) => {
  switch (role) {
    case 'superadmin':
      return 'Super Admin';
    case 'branch_admin':
      return 'Branch Admin';
    case 'owner':
      return 'Owner';
    default:
      return role;
  }
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

export function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    id: string;
    name: string;
    username: string;
    role: 'superadmin' | 'branch_admin' | 'owner';
  } | null>(null);

  // Fetch user by ID (keeps UI static, only fills required fields)
  const { data: apiUser } = useQuery<ServiceUser, Error>({
    queryKey: ['user', id],
    queryFn: () => userManagementAPI.getUserById(id!),
    enabled: Boolean(id),
  });

  // Static fallback data (unchanged layout)
  const user: User = {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'superadmin',
    branch: 'Main Branch',
    avatar: 'https://github.com/shadcn.png',
    isActive: true,
    lastLogin: '2024-01-15 14:30',
    createdAt: '2024-01-15 14:30',
    phone: '+1234567890',
    department: 'General',
    permissions: [
      'View Dashboard',
      'Manage Products',
      'View Reports',
      'Manage Users',
      'System Settings',
    ],
  };

  const displayRole = apiUser?.roles?.[0] as User['role'];

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userManagementAPI.deleteUser(userId),
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteModalOpen(false);
      navigate('/user-management');
    },
    onError: (error: unknown) => {
      const maybeAxiosError = error as {
        response?: { data?: { message?: string } };
      };
      const message =
        maybeAxiosError?.response?.data?.message || 'Failed to delete user';
      toast.error(message);
    },
  });

  const onBack = () => {
    navigate('/user-management');
  };
  const onEdit = () => {
    navigate(`/user-management/edit/${id}`);
  };
  const onDelete = () => {
    if (!id) return;
    const role: 'superadmin' | 'owner' | 'branch_admin' =
      apiUser?.roles?.includes('superadmin')
        ? 'superadmin'
        : apiUser?.roles?.includes('owner')
          ? 'owner'
          : 'branch_admin';
    setUserToDelete({
      id: String(id),
      name: apiUser
        ? `${apiUser.firstName} ${apiUser.lastName}`.trim() || apiUser.username
        : user.name,
      username: apiUser ? apiUser.username : user.email,
      role,
    });
    setDeleteModalOpen(true);
  };
  const onChat = () => {
    console.log('chat');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-0 justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <h1 className="text-xl md:text-2xl font-semibold">User Details</h1>
            <p className="text-muted-foreground hidden md:block">
              View and manage user information
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit User
          </Button>
          <Button variant="destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete User
          </Button>
          {onChat && (
            <Button variant="outline" onClick={onChat}>
              <MessageCircle className="h-4 w-4 mr-2" />
              Chat
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage
                    src={`${import.meta.env.VITE_API_STORAGE_URL}${apiUser?.image}`}
                    className="object-cover"
                    alt={apiUser?.firstName || ''}
                  />
                  <AvatarFallback>
                    {getInitials(apiUser?.firstName || '')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium">
                      {apiUser?.firstName || ''} {apiUser?.lastName || ''}
                    </h3>
                    <Badge className={getStatusColor(apiUser?.isActive)}>
                      {apiUser?.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      {apiUser?.username || ''}
                    </div>
                    {apiUser?.phoneNumber && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {apiUser?.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Role
                    </label>
                    <div className="mt-1">
                      <Badge className={getRoleColor(displayRole)}>
                        {formatRole(displayRole)}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Branch
                    </label>
                    <p className="mt-1">{user.branch || 'Not assigned'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Created Date
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{apiUser?.createdAt}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Last Login
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{apiUser?.updatedAt}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Logs */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockActivityLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">
                        {log.action}
                      </TableCell>
                      <TableCell>{log.description}</TableCell>
                      <TableCell>
                        <Badge className={getActivityTypeColor(log.type)}>
                          {log.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.timestamp}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card> */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Permissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {permissions.map((permission, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-green-600" />
                    <span className="text-sm">{permission}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card> */}

          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Products Managed
                </span>
                <span className="font-medium">24</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Orders Processed
                </span>
                <span className="font-medium">156</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Login Sessions
                </span>
                <span className="font-medium">89</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Average Session
                </span>
                <span className="font-medium">2h 15m</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <UserDeleteModal
        user={userToDelete}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={() =>
          userToDelete && deleteUserMutation.mutate(userToDelete.id)
        }
        isDeleting={deleteUserMutation.isPending}
      />
    </div>
  );
}
